






































































import OtButton from '@/components/global/ot-button.vue';
import OtLoadingSpinner from '@/components/global/ot-loading-spinner.vue';
import OtRadioGroup, { IRadioGroupOption } from '@/components/global/ot-radio-group.vue';
import OtTableHeader from '@/components/global/table/ot-table-header.vue';
import { IColumnData } from '@/components/global/table/ot-table-models';
import { handleHeaderClick } from '@/components/global/table/ot-table-utils';
import OtTable from '@/components/global/table/ot-table.vue';
import OtApi, { executeApi } from '@/services/api.service';
import {
  AconexInteractionStatus,
  GetSearchAconexMailResponseMailModel as ApiGetSearchAconexMailResponseMailModel,
  GetSearchAconexMailResponseRecipientModel as ApiGetSearchAconexMailResponseRecipientModel,
  GetSearchAconexMailResponseUserModel as ApiGetSearchAconexMailResponseUserModel,
} from '@/services/generated/api';
import { vxm } from '@/store';
import { getAconexMailLink } from '@/utils/aconex-utils';
import { formatDateAndTime } from '@/utils/date-utils';
import { Component, Prop, Vue } from 'vue-property-decorator';

type SelectableItem = {
  key: string;
  item: ApiGetSearchAconexMailResponseMailModel; // some kind of aconex data type
};

const MAIL_BOX_OPTION_INBOX: IRadioGroupOption = {
  label: 'Inbox',
  key: 'inbox',
};

const MAIL_BOX_OPTION_SENTBOX: IRadioGroupOption = {
  label: 'Sentbox',
  key: 'sentbox',
};

@Component({
  components: {
    OtTableHeader,
    OtTable,
    OtLoadingSpinner,
    OtRadioGroup,
    OtButton,
  },
})
export default class OtAconexMailList extends Vue {
  // * PROPS
  @Prop({ required: true }) private aconexProjectId!: string;

  // * REFS

  // * DATA
  /* from API17060 GET Search Aconex Mail, fields available for sorting are
    * confidential
    * corrtypeid
    * docno
    * inreftomailno
    * reasonforissueid
    * responsedate
    * sentdate
    * subject
    So, that's what we're setting the keys to. If it's sortable, the key is from above. If it's not sortable, the key doesn't matter.
  */
  private tableColumnsBase: IColumnData[] = [
    {
      index: 0,
      label: '',
      key: 'selectorColumn',
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 1,
      label: 'Mail No',
      key: 'docno', // don't change this, the API expects this exact string for the sorting
      isActive: true,
      ascending: true,
      sortable: true,
    },
    {
      index: 2,
      label: 'Subject',
      key: 'subject', // don't change this, the API expects this exact string for the sorting
      isActive: false,
      ascending: false,
      sortable: true,
    },
    {
      index: 3,
      label: 'Date Sent',
      key: 'sentdate', // don't change this, the API expects this exact string for the sorting
      isActive: false,
      ascending: false,
      sortable: true,
    },
    {
      index: 4,
      label: 'From (Organisation)',
      key: 'fromorganisation', // Not sortable, doesn't really matter
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 5,
      label: 'To (Organisation)',
      key: 'toorganisation', // Not sortable, doesn't really matter
      isActive: false,
      ascending: false,
      sortable: false,
    },
  ];
  private tableColumns: IColumnData[] = [...this.tableColumnsBase];
  private activeColumn = this.tableColumnsBase[1];
  private page = 1;
  private listItems: SelectableItem[] = [];
  private totalPages = 1;
  private api = new OtApi();

  private mailBoxOptions: IRadioGroupOption[] = [MAIL_BOX_OPTION_INBOX, MAIL_BOX_OPTION_SENTBOX];

  // * COMPUTED
  private searchTextPrivate = '';
  private get searchText() {
    return this.searchTextPrivate;
  }
  private set searchText(value: string) {
    this.searchTextPrivate = value;
    // back to the first page
    this.updateCurrentPage(1);
  }

  private selectedMailBoxPrivate = MAIL_BOX_OPTION_INBOX;
  private get selectedMailBox() {
    return this.selectedMailBoxPrivate;
  }
  private set selectedMailBox(value: IRadioGroupOption) {
    this.selectedMailBoxPrivate = value;
    // back to the first page
    this.updateCurrentPage(1);
  }

  private get aconexLink() {
    // in theory this could be undefined, so we have the fallback. But, if you're in the middle of an import, it better well not be
    return (
      vxm.userProfile.userProfile?.aconexUserInformation?.userSite ||
      'https://constructionandengineering.oraclecloud.com/web/home'
    );
  }

  private getAconexMailLink(mail: ApiGetSearchAconexMailResponseMailModel) {
    return getAconexMailLink({
      mailId: mail.mailId,
      projectId: this.aconexProjectId,
      // we are doing the import, so we know what site it is for. It's not on the search results yet
      originalAconexSite: vxm.userProfile.userProfile?.aconexUserInformation?.userSite ?? null,
    });
  }

  // * WATCHERS

  // * METHODS
  private updateCurrentPage(value: number) {
    // clear the selected item
    // we could try and keep it, but it doesn't seem to work at first glance. This way it'll be predictable
    this.setSelectedKey('');

    // reload the new page of results
    this.page = value;
    this.loadResults();
  }

  private headerClick(header: IColumnData) {
    const handledHeaders = handleHeaderClick(this.tableColumns, this.activeColumn, header);
    this.tableColumns = handledHeaders.tableColumns;
    this.activeColumn = handledHeaders.activeColumn;
    // go back to the first page
    this.updateCurrentPage(1);
  }

  private isLoading = false;
  private mostRecentLoadKey: symbol | null = null;
  private searchDebounceTimeout: number | null = null;

  // This is not awaitable. There's debouncing and I can't brain how to return a promise when there is debouncing
  // for now, we don't need to await it. If we ever did need to await it, consinder returning a promise and resolving
  // it when the debounce timeout fires. Or something. I don't know this morning, I'm not googling, we don't need it yet
  // implement it later if we need it
  private loadResults() {
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
      this.searchDebounceTimeout = null;
    }
    this.searchDebounceTimeout = setTimeout(this.loadResultsInner, 500);
  }

  private async loadResultsInner() {
    this.isLoading = true;

    let searchQuery = '';
    if (this.searchText) {
      // we're going to split on spaces, then craft the lucene query
      // if the user types in "foo bar" we want to search for "foo*" AND "bar*"
      // if the user types in "foo" we want to search for "foo*"
      // if the user types in "foo bar baz" we want to search for "foo*" AND "bar*" AND "baz*"
      // we need to search across a few fields
      // the finished string should look a bit like
      // title:(Dev* AND Gui*) OR docno:(Dev* AND Gui*) OR author:(Dev* AND Gui*)
      // for a single word, it'll be overkill with the brackets, but it'll work

      const rawSearchTerms = this.searchText.split(' ');
      const processedSearchTerms = rawSearchTerms.map(x => `${x}*`);
      const processedSearchTermsString = processedSearchTerms.join(' AND ');
      searchQuery = `docno:(${processedSearchTermsString}) OR subject:(${processedSearchTermsString}) OR fromuserfullname:(${processedSearchTermsString}) OR touserfullname:(${processedSearchTermsString})`;
    }

    // the key is optional and not needed, but I feel like it's nice to give it a description
    const thisRequestLoadKey = Symbol('get-search-mail');
    this.mostRecentLoadKey = thisRequestLoadKey;

    const apiResult = await executeApi(
      () =>
        this.api.aconex().getSearchAconexMail(
          //  * @param projectId Aconex Project Id originally obtained from call to <seealso cref="M:OneTrack.Api.V1.AconexController.GetAconexProjects(OneTrack.Api.Commands.Aconex.IGetAconexProjectsCommand,System.Threading.CancellationToken)">API17056 - GET Aconex Projects</seealso>
          this.aconexProjectId,
          //  * @param mailBox One of:
          // <list type="bullet"><item>inbox</item><item>sentbox</item></list> (There is no need for a OneTrack user to be searching draftbox.)
          this.selectedMailBox.key,
          //  * @param query (optional) Lucene search query.
          // Field names available for searching:
          // <list type="bullet"><item>attribute </item><item>secondaryattribute </item><item>thirdattribute </item><item>fourthattribute </item><item>closedoutdate </item><item>corrdata </item><item>corrtypeid </item><item>docno </item><item>fromtradingname </item><item>fromuserfullname </item><item>inreftomailno </item><item>reasonforissueid </item><item>responsedate </item><item>sentdate </item><item>subject </item><item>tostatusid </item><item>totradingname </item><item>touserfullname </item></list>
          // Note: Without a search_query, every document in the project is returned.
          searchQuery,
          //  * @param pageSize (optional) If specified, the value must be a number that is divisible by 25. Without this parameter specified, this defaults to 25.
          25,
          // Note: This parameter has a maximum value of 500
          //  * @param pageNumber (optional) If specified, the value must be between "1" and the value of the "TotalPages" attribute that is in the response, otherwise an error is returned. Without this parameter specified, this defaults to 1.
          this.page,
          //  * @param sortField (optional) The name of the field to sort the search results by. Results can only be sorted by a single field, although it does not have to be one of the fields specified in the return_fields parameter.
          // <list><item>confidential </item><item>corrtypeid </item><item>docno </item><item>inreftomailno </item><item>reasonforissueid </item><item>responsedate </item><item>sentdate </item><item>subject </item></list>
          // Field names available for sorting are:
          this.activeColumn.sortable ? this.activeColumn.key : undefined,
          //  * @param sortDirection (optional) One of:
          // <list type="bullet"><item>ASC, ascending order </item><item>DESC, descending order </item></list>
          this.activeColumn.sortable ? (this.activeColumn.ascending ? 'ASC' : 'DESC') : undefined,
        ),
      'Searching Aconex Mail',
    );
    if (this.mostRecentLoadKey !== thisRequestLoadKey) {
      // Another request has been made - the module level variable has been set to something else
      // Drop these results on the floor
      // Don't worry about setting isLoading either, we know there is another request happening
      return;
    }

    if (apiResult.success) {
      if (apiResult.data && apiResult.data.aconexInteractionStatus === AconexInteractionStatus.Success) {
        this.listItems =
          apiResult.data.result?.searchResults.map(x => ({
            key: x.mailId,
            isSelected: false,
            item: x,
          })) ?? [];
        this.totalPages = apiResult.data.result?.totalPages ?? 1;
      } else {
        console.warn('Aconex Mail Search Failed', apiResult.data);
        this.listItems = [];
      }
    }
    this.isLoading = false;
  }

  private displayUser(user: ApiGetSearchAconexMailResponseUserModel) {
    return `${user.firstName} ${user.lastName} (${user.organizationName})`;
  }
  private displayUsers(users: ApiGetSearchAconexMailResponseRecipientModel[]) {
    // could also use a line break. This is more compact, hard to read though
    // Could bust these out by the TO CC BCC as well
    return users.map(u => `${u.distributionType}: ${this.displayUser(u)}`).join(',');
  }
  private formatDateAndTime(date: Date) {
    return formatDateAndTime(date);
  }
  private selectedKey = '';
  private setSelectedKey(key: string) {
    this.selectedKey = key;
    if (key) {
      const selectedItem = this.listItems.find(x => x.key === key);
      if (selectedItem) {
        this.$emit('select', selectedItem.item);
      } else {
        console.warn('ot-aconex-mail-list: setSelectedKey: selectedItem not found', { items: this.listItems, key });
      }
    } else {
      this.$emit('select', null);
    }
  }

  // * LIFECYCLE
  private async mounted() {
    console.log('ot-aconex-mail-list: mounted');
    this.loadResultsInner();
  }
}
